// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Types
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import {
	IAdministrationTools,
	IKmsStore,
	IUpdatedAdministrationToolsPayrollProvidersResponse,
	IManagementSystem,
	TUpdateAdministrationToolsManagementSystemResponse,
} from "@Settings/types/administrationTools/model";
import { AuthenticationType, AuthenticationTypes, BasicAuth, TokenAuth } from "@Settings/types/Common/auth";
import { CustomCredentials, EManagementSystem, TGpsStore, TSelectedProvider } from "@Settings/types/administrationTools/utils";
import { AdministrationToolsIntegrationsFragment } from "@Settings/api/AdministrationTools/fragments";

export async function updateStorePicture(locationId: string, operation: string, input: unknown, indexToModify: number) {
	const query = gql`
		mutation updateStorePicture($locationId: String!, $operation: String!, $input: StorePictureInput, $indexToModify: Int) {
			updateStorePicture(locationId: $locationId, operation: $operation, input: $input, indexToModify: $indexToModify) {
				locationId
				storePictures {
					pictureURL
				}
			}
		}
	`;

	const variables = {
		locationId,
		operation,
		input,
		indexToModify,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateStorePictureTest;
	} catch (err) {
		errorLogger(err as Error, { function: "updateStorePicture", message: "Failed to update store picture", variables });
		return null;
	}
}

export async function updateAdministrationTools(locationId: string, input: Partial<IAdministrationTools>) {
	const query = gql`
		mutation updateAdministrationTools($locationId: String!, $input: AdministrationToolsInput) {
			updateAdministrationTools(locationId: $locationId, input: $input) {
				_id
				locationId
				integrations {
					...Integrations
				}
			}
		}
		${AdministrationToolsIntegrationsFragment}
	`;

	const variables = {
		locationId,
		input,
	};
	const response: GraphqlQueryResponse<"updateAdministrationTools", Partial<IAdministrationTools>> = await apiInstance.post("/graphql", {
		query,
		variables,
	});
	return response.data.data?.updateAdministrationTools;
}

export async function updateAdministrationToolsManagementSystem(
	locationId: string,
	managementSystemType: EManagementSystem,
	managementSystemInput: IManagementSystem
) {
	const query = gql`
		mutation updateAdministrationToolsManagementSystem(
			$locationId: String!
			$managementSystemType: String!
			$managementSystemInput: ManagementSystemInput!
		) {
			updateAdministrationToolsManagementSystem(
				locationId: $locationId
				managementSystemType: $managementSystemType
				managementSystemInput: $managementSystemInput
			) {
				locationId
				managementSystemType
				managementSystem {
					providerName
					provider
					systemTenantId
					dataSource
					userID
					hasDealerNumber
					storeNumber
					areaNumber
				}
			}
		}
	`;

	const variables = {
		locationId,
		managementSystemType,
		managementSystemInput,
	};
	const response: GraphqlQueryResponse<"updateAdministrationToolsManagementSystem", TUpdateAdministrationToolsManagementSystemResponse> =
		await apiInstance.post("/graphql", {
			query,
			variables,
		});
	return response.data.data?.updateAdministrationToolsManagementSystem;
}

export async function updateReynoldsInventoryMapping(locationId: string, inputMapping: unknown) {
	const query = gql`
		mutation updateReynoldsInventoryMapping($locationId: String!, $input: ReynoldsInventoryMappingObjectInput!) {
			updateReynoldsInventoryMapping(locationId: $locationId, input: $input) {
				_id
				locationId
				integrations {
					reynoldsInventoryMapping {
						new {
							_id
							name
							mappedName
							isEditable
							isActive
							isCustom
						}
						preOwned {
							_id
							name
							mappedName
							isEditable
							isActive
							isCustom
						}
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		input: inputMapping,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateReynoldsInventoryMapping;
	} catch (err) {
		errorLogger(err as Error, {
			function: "updateReynoldsInventoryMapping",
			message: "Failed to update reynolds inventory mapping",
			variables,
		});
		return null;
	}
}

type LoginProviderArgs = {
	locationId: string;
	provider: TSelectedProvider;
	integrationProviderProperty: string;
	credentials: CustomCredentials;
};

export async function loginProvider<TData>({
	locationId,
	provider,
	credentials,
	integrationProviderProperty,
}: LoginProviderArgs): Promise<TData | null> {
	const { providerName, providerDBProperty, authType: authenticationType, auth } = provider;
	const { baseUrl, httpMethod, endpoint } = auth;
	const query = gql`
		mutation loginProvider($locationId: String!, $integrationProviderProperty: String!, $input: ProviderLoginInput!) {
			loginProvider(locationId: $locationId, integrationProviderProperty: $integrationProviderProperty, input: $input) {
				_id
				locationId
				integrations {
					...Integrations
				}
			}
		}
		${AdministrationToolsIntegrationsFragment}
	`;

	const getVariables = (
		authenticationTypeVariant: AuthenticationType,
		credentialsObj: CustomCredentials
	): { input: AuthenticationTypes } => {
		if (authenticationTypeVariant === "basicAuth") {
			const { username, password } = credentialsObj as BasicAuth;
			return { input: { username, password } };
		} else if (authenticationTypeVariant === "tokenAuth") {
			const { token } = credentialsObj as TokenAuth;
			return { input: { token } };
		}
		return { input: {} } as { input: AuthenticationTypes };
	};

	const credentialsInput = getVariables(authenticationType as AuthenticationType, credentials);
	const variables = {
		locationId,
		integrationProviderProperty,
		input: {
			credentials: {
				...credentialsInput.input,
			},
			operation: {
				...credentials?.operation,
				baseUrl,
				endpoint,
				httpMethod,
				authType: authenticationType,
				properties: provider?.auth?.roles?.find((role) => role.role === credentials?.operation?.role)?.properties ?? "",
			},
			providerName,
			providerDBProperty,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.loginProvider ?? null;
	} catch (err) {
		errorLogger(err as Error, {
			function: "loginProvider",
			message: "Failed to login provider",
			variables: {
				locationId,
				providerName,
				authenticationType,
				integrationProviderProperty,
				credentials: credentialsInput,
			},
		});
		return null;
	}
}

export async function updateGpsStore(locationId: string, providerDBProperty: string, gpsStoreInput: TGpsStore) {
	const query = gql`
		mutation updateGpsStore($locationId: String!, $providerDBProperty: String!, $input: GpsStoreInput) {
			updateGpsStore(locationId: $locationId, providerDBProperty: $providerDBProperty, input: $input) {
				_id
				locationId
				integrations {
					...Integrations
				}
			}
		}
		${AdministrationToolsIntegrationsFragment}
	`;

	const variables = {
		locationId,
		providerDBProperty,
		input: gpsStoreInput,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateGpsStore ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "updateGpsStore", message: "Failed to update GPS store", variables });
		return null;
	}
}
export async function deleteGpsLot(locationId: string, lotId: string) {
	const query = gql`
		mutation deleteGpsLot($locationId: String!, $lotId: String!) {
			deleteGpsLot(locationId: $locationId, lotId: $lotId) {
				locationId
				lotId
			}
		}
	`;
	const variables = {
		locationId,
		lotId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteGpsLot;
	} catch (error) {
		errorLogger(error as Error, { function: "deleteGpsLot", message: "Failed to delete GPS lot", variables });
		return null;
	}
}
export async function updateGpsLots(locationId: string, providerDBProperty: string, gpsStoreId: string) {
	const query = gql`
		mutation updateGpsLots($locationId: String!, $providerDBProperty: String!, $gpsStoreId: String!) {
			updateGpsLots(locationId: $locationId, providerDBProperty: $providerDBProperty, gpsStoreId: $gpsStoreId)
		}
	`;

	const variables = {
		locationId,
		providerDBProperty,
		gpsStoreId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateGpsStore;
	} catch (error) {
		errorLogger(error as Error, { function: "updateGpsLots", message: "Failed to update GPS lots", variables });
		return null;
	}
}

export async function requestClientAccessSendEmail(locationId: string, providerName: string, emailTo: string, approvalLink: string) {
	const query = gql`
		mutation requestClientAccessSendEmail($locationId: String!, $providerName: String!, $emailTo: String!, $approvalLink: String!) {
			requestClientAccessSendEmail(
				locationId: $locationId
				providerName: $providerName
				emailTo: $emailTo
				approvalLink: $approvalLink
			)
		}
	`;
	const variables = {
		locationId,
		providerName,
		emailTo,
		approvalLink,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.requestClientAccessSendEmail;
	} catch (error) {
		errorLogger(error as Error, { function: "requestClientAccessSendEmail", message: "Failed to send email", variables });
		return null;
	}
}

/********************************************* INTEGRATIONS ************************************************/

/********************************************* KMS ************************************************/
export type TLoginKMSProvider = {
	locationId: string;
	provider: string;
	providerName: string;
	providerDBProperty: string;
	credentials: AuthenticationTypes;
	operation: {
		role: string;
	};
};
export async function loginKMSProvider(args: TLoginKMSProvider) {
	const { locationId, provider, providerName, providerDBProperty, credentials, operation } = args;
	const query = gql`
		mutation loginKMSProvider($locationId: String!, $input: LoginKMSProviderInput) {
			loginKMSProvider(locationId: $locationId, input: $input) {
				_id
				locationId
				integrations {
					...Integrations
				}
			}
		}
		${AdministrationToolsIntegrationsFragment}
	`;

	const variables = {
		locationId,
		input: {
			provider,
			providerName,
			providerDBProperty,
			credentials,
			operation,
		},
	};
	try {
		const response: GraphqlQueryResponse<"loginKMSProvider", unknown> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.loginKMSProvider ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "loginKMSProvider", message: "Failed to login KMS provider", variables: args });
		return null;
	}
}

export type TLogoutKMSProvider = {
	locationId: string;
	provider: string;
	providerDBProperty: string;
	operation: {
		role: string;
	};
};
export async function logoutKMSProvider(args: TLogoutKMSProvider) {
	const { locationId, provider, providerDBProperty, operation } = args;
	const query = gql`
		mutation logoutKMSProvider($locationId: String!, $input: LogoutKMSProviderInput) {
			logoutKMSProvider(locationId: $locationId, input: $input) {
				_id
				locationId
				integrations {
					...Integrations
				}
			}
		}
		${AdministrationToolsIntegrationsFragment}
	`;

	const variables = {
		locationId,
		input: {
			provider,
			providerDBProperty,
			operation,
		},
	};
	try {
		const response: GraphqlQueryResponse<"logoutKMSProvider", unknown> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.logoutKMSProvider ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "logoutKMSProvider", message: "Failed to logout KMS provider", variables: args });
		return null;
	}
}

export type TUpdateKMSStoreAndKioskConfigurationInputArgs = {
	provider: string;
	providerDBProperty: string;
	kmsStores: IKmsStore[];
};
export async function updateKMSStoreAndKioskConfiguration(locationId: string, input: TUpdateKMSStoreAndKioskConfigurationInputArgs) {
	const query = gql`
		mutation updateKMSStoreAndKioskConfiguration($locationId: String!, $input: UpdateKMSStoreAndKioskConfigurationInput!) {
			updateKMSStoreAndKioskConfiguration(locationId: $locationId, input: $input) {
				_id
				locationId
				integrations {
					...Integrations
				}
			}
		}
		${AdministrationToolsIntegrationsFragment}
	`;

	const variables = {
		locationId,
		input,
	};
	try {
		const response: GraphqlQueryResponse<"updateKMSStoreAndKioskConfiguration", IAdministrationTools> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.updateKMSStoreAndKioskConfiguration ?? null;
	} catch (err) {
		errorLogger(err as Error, {
			function: "updateKMSStoreAndKioskConfiguration",
			message: "Failed to update KMS store and kiosk configuration",
			variables: { locationId, input },
		});
		return null;
	}
}

/********************************************* END KMS ************************************************/

export async function deletePayrollProvidersAwaitingAccounts(locationId: string, providerName: string, displayId: string) {
	const query = gql`
		mutation deletePayrollProvidersAwaitingAccounts($locationId: String!, $providerName: String!, $displayId: String!) {
			deletePayrollProvidersAwaitingAccounts(locationId: $locationId, providerName: $providerName, displayId: $displayId) {
				locationId
				payrollProviders {
					providerName
					configuredAccounts {
						companyId
						displayId
						status
						statusDate
					}
				}
			}
		}
	`;
	const variables = {
		locationId,
		providerName,
		displayId,
	};

	try {
		const response: GraphqlQueryResponse<
			"deletePayrollProvidersAwaitingAccounts",
			IUpdatedAdministrationToolsPayrollProvidersResponse
		> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deletePayrollProvidersAwaitingAccounts;
	} catch (error) {
		errorLogger(error as Error, {
			function: "deletePayrollProvidersAwaitingAccounts",
			message: "Failed to delete awaiting accounts",
			variables,
		});
		return null;
	}
}
